import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

function RechnungenManager() {
    const { authFetch } = useAuth();
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('invoices');
    const [message, setMessage] = useState(null);

    // Produktkatalog
    const [products, setProducts] = useState([]);
    const [editingProduct, setEditingProduct] = useState(null);
    const [newProduct, setNewProduct] = useState({ name: '', description: '', category: '', price: '', unit: 'Stueck' });

    // Events fuer Import (alle Typen)
    const [feriencamps, setFeriencamps] = useState([]);
    const [workshops, setWorkshops] = useState([]);

    // Mitglieder
    const [members, setMembers] = useState([]);
    const [memberSearch, setMemberSearch] = useState('');
    const [selectedMember, setSelectedMember] = useState(null);

    // Freie Rechnung
    const [manualCustomerName, setManualCustomerName] = useState('');
    const [manualCustomerEmail, setManualCustomerEmail] = useState('');
    const [manualCustomerAddress, setManualCustomerAddress] = useState('');
    const [manualItems, setManualItems] = useState([]);
    const [manualDiscount, setManualDiscount] = useState('');
    const [manualDiscountReason, setManualDiscountReason] = useState('');
    const [manualNotes, setManualNotes] = useState('');
    const [manualApplyVat, setManualApplyVat] = useState(false);
    const [manualVatRate, setManualVatRate] = useState('19');

    // Schnell-Positionen
    const [freeItemDesc, setFreeItemDesc] = useState('');
    const [freeItemAmount, setFreeItemAmount] = useState('');
    const [quickMealDesc, setQuickMealDesc] = useState('');
    const [quickMealAmount, setQuickMealAmount] = useState('');
    const [quickOtherDesc, setQuickOtherDesc] = useState('');
    const [quickOtherAmount, setQuickOtherAmount] = useState('');

    useEffect(() => {
        fetchInvoices();
        fetchProducts();
        fetchEvents();
        fetchMembers();
    }, []);

    const fetchInvoices = () => {
        authFetch('/api/invoices')
            .then(res => res.json())
            .then(data => setInvoices(data.data || []))
            .catch(err => console.error('Fehler beim Laden der Rechnungen:', err));
    };

    const fetchProducts = () => {
        authFetch('/api/invoice-products')
            .then(res => res.json())
            .then(data => setProducts(data.data || []))
            .catch(err => console.error('Fehler beim Laden der Produkte:', err));
    };

    const fetchEvents = () => {
        // Feriencamps laden
        authFetch('/api/feriencamp-events')
            .then(res => res.json())
            .then(data => setFeriencamps(data.data || []))
            .catch(err => console.error('Fehler beim Laden der Feriencamps:', err));

        // Workshops laden (inkl. Female AI Night, etc.)
        authFetch('/api/workshops')
            .then(res => res.json())
            .then(data => setWorkshops(data.data || []))
            .catch(err => console.error('Fehler beim Laden der Workshops:', err));
    };

    const fetchMembers = () => {
        authFetch('/api/members')
            .then(res => res.json())
            .then(data => setMembers(data.data || []))
            .catch(err => console.error('Fehler beim Laden der Mitglieder:', err));
    };

    // Mitglied auswaehlen
    const selectMember = (member) => {
        setSelectedMember(member);
        setManualCustomerName(`${member.first_name} ${member.last_name}`);
        setManualCustomerEmail(member.email);
        setManualCustomerAddress(member.address || '');
        setMemberSearch('');
    };

    // Mitglied-Auswahl aufheben
    const clearMember = () => {
        setSelectedMember(null);
        setManualCustomerName('');
        setManualCustomerEmail('');
        setManualCustomerAddress('');
    };

    // Gefilterte Mitglieder
    const filteredMembers = memberSearch.length >= 2
        ? members.filter(m =>
            `${m.first_name} ${m.last_name}`.toLowerCase().includes(memberSearch.toLowerCase()) ||
            m.email.toLowerCase().includes(memberSearch.toLowerCase())
        ).slice(0, 5)
        : [];

    // Position hinzufuegen
    const addItem = (description, price, quantity = 1, category = '') => {
        if (!description || !price) return;
        setManualItems([...manualItems, { description, quantity, price: parseFloat(price), category }]);
    };

    // Position entfernen
    const removeItem = (index) => {
        setManualItems(manualItems.filter((_, i) => i !== index));
    };

    // Produkt aus Katalog hinzufuegen
    const addProductToInvoice = (product) => {
        addItem(product.name, product.price, 1, product.category);
    };

    // Feriencamp als Position hinzufuegen
    const addFeriencampToInvoice = (event) => {
        const dateStr = formatDate(event.start_date);
        addItem(`${event.title} (${dateStr})`, event.price, 1, 'Feriencamp');
    };

    // Workshop als Position hinzufuegen
    const addWorkshopToInvoice = (workshop) => {
        const dateStr = workshop.date ? formatDate(workshop.date) : (workshop.dates_display || '');
        const desc = dateStr ? `${workshop.title} (${dateStr})` : workshop.title;
        addItem(desc, workshop.price, 1, 'Workshop');
    };

    // Freie Position hinzufuegen
    const addFreeItem = () => {
        if (freeItemDesc && freeItemAmount) {
            addItem(freeItemDesc, freeItemAmount, 1, '');
            setFreeItemDesc('');
            setFreeItemAmount('');
        }
    };

    // Essen hinzufuegen
    const addMeal = () => {
        if (quickMealDesc && quickMealAmount) {
            addItem(quickMealDesc, quickMealAmount, 1, 'Essen');
            setQuickMealDesc('');
            setQuickMealAmount('');
        }
    };

    // Sonstige Kosten hinzufuegen
    const addOther = () => {
        if (quickOtherDesc && quickOtherAmount) {
            addItem(quickOtherDesc, quickOtherAmount, 1, 'Sonstiges');
            setQuickOtherDesc('');
            setQuickOtherAmount('');
        }
    };

    // Netto-Zwischensumme (nach Rabatt, vor MwSt.) berechnen
    const calculateNetTotal = () => {
        const subtotal = manualItems.reduce((sum, item) => {
            return sum + (parseFloat(item.quantity) || 1) * (parseFloat(item.price) || 0);
        }, 0);
        const discount = parseFloat(manualDiscount) || 0;
        return subtotal - discount;
    };

    // MwSt.-Betrag berechnen
    const calculateVatAmount = () => {
        if (!manualApplyVat) return 0;
        const rate = parseFloat(manualVatRate) || 0;
        return Math.round(calculateNetTotal() * rate) / 100;
    };

    // Gesamtbetrag (inkl. MwSt. falls aktiviert) berechnen
    const calculateTotal = () => {
        return calculateNetTotal() + calculateVatAmount();
    };

    // Rechnung erstellen
    const createInvoice = () => {
        if (!manualCustomerName || !manualCustomerEmail) {
            setMessage({ type: 'error', text: 'Name und E-Mail sind erforderlich' });
            return;
        }

        if (manualItems.length === 0) {
            setMessage({ type: 'error', text: 'Mindestens eine Position erforderlich' });
            return;
        }

        setLoading(true);
        authFetch('/api/invoices/manual', {
            method: 'POST',
            body: {
                customer_name: manualCustomerName,
                customer_email: manualCustomerEmail,
                customer_address: manualCustomerAddress || null,
                member_id: selectedMember?.id || null,
                items: manualItems,
                discount: manualDiscount ? parseFloat(manualDiscount) : 0,
                discount_reason: manualDiscountReason || null,
                notes: manualNotes || null,
                apply_vat: manualApplyVat,
                vat_rate: manualApplyVat ? (parseFloat(manualVatRate) || 19) : null
            }
        })
            .then(res => res.json())
            .then(data => {
                if (data.message === 'success') {
                    setMessage({ type: 'success', text: `Rechnung ${data.data.invoice_number} erstellt!` });
                    fetchInvoices();
                    // Formular zuruecksetzen
                    clearMember();
                    setManualItems([]);
                    setManualDiscount('');
                    setManualDiscountReason('');
                    setManualNotes('');
                    setManualApplyVat(false);
                    setManualVatRate('19');
                } else {
                    setMessage({ type: 'error', text: data.error || 'Fehler' });
                }
            })
            .catch(err => setMessage({ type: 'error', text: err.message }))
            .finally(() => setLoading(false));
    };

    // Produktkatalog - Produkt erstellen
    const createProduct = () => {
        if (!newProduct.name || !newProduct.price) {
            setMessage({ type: 'error', text: 'Name und Preis sind erforderlich' });
            return;
        }

        authFetch('/api/invoice-products', {
            method: 'POST',
            body: newProduct
        })
            .then(res => res.json())
            .then(data => {
                if (data.message === 'success') {
                    setMessage({ type: 'success', text: 'Produkt erstellt!' });
                    fetchProducts();
                    setNewProduct({ name: '', description: '', category: '', price: '', unit: 'Stueck' });
                } else {
                    setMessage({ type: 'error', text: data.error || 'Fehler' });
                }
            })
            .catch(err => setMessage({ type: 'error', text: err.message }));
    };

    // Produktkatalog - Feriencamp als Produkt importieren
    const importFeriencampAsProduct = (event) => {
        const productData = {
            name: event.title,
            description: `${formatDate(event.start_date)} - ${formatDate(event.end_date)}`,
            category: 'Feriencamp',
            price: event.price,
            unit: 'Pauschal'
        };

        authFetch('/api/invoice-products', {
            method: 'POST',
            body: productData
        })
            .then(res => res.json())
            .then(data => {
                if (data.message === 'success') {
                    setMessage({ type: 'success', text: `"${event.title}" zum Katalog hinzugefuegt!` });
                    fetchProducts();
                } else {
                    setMessage({ type: 'error', text: data.error || 'Fehler' });
                }
            })
            .catch(err => setMessage({ type: 'error', text: err.message }));
    };

    // Produktkatalog - Workshop als Produkt importieren
    const importWorkshopAsProduct = (workshop) => {
        const dateStr = workshop.date ? formatDate(workshop.date) : (workshop.dates_display || '');
        const productData = {
            name: workshop.title,
            description: dateStr,
            category: 'Workshop',
            price: workshop.price,
            unit: 'Pauschal'
        };

        authFetch('/api/invoice-products', {
            method: 'POST',
            body: productData
        })
            .then(res => res.json())
            .then(data => {
                if (data.message === 'success') {
                    setMessage({ type: 'success', text: `"${workshop.title}" zum Katalog hinzugefuegt!` });
                    fetchProducts();
                } else {
                    setMessage({ type: 'error', text: data.error || 'Fehler' });
                }
            })
            .catch(err => setMessage({ type: 'error', text: err.message }));
    };

    // Produktkatalog - Produkt aktualisieren
    const updateProduct = (product) => {
        authFetch(`/api/invoice-products/${product.id}`, {
            method: 'PUT',
            body: product
        })
            .then(res => res.json())
            .then(data => {
                if (data.message === 'success') {
                    setMessage({ type: 'success', text: 'Produkt aktualisiert!' });
                    fetchProducts();
                    setEditingProduct(null);
                } else {
                    setMessage({ type: 'error', text: data.error || 'Fehler' });
                }
            })
            .catch(err => setMessage({ type: 'error', text: err.message }));
    };

    // Produktkatalog - Produkt loeschen
    const deleteProduct = (productId) => {
        if (!window.confirm('Produkt wirklich loeschen?')) return;

        authFetch(`/api/invoice-products/${productId}`, { method: 'DELETE' })
            .then(res => res.json())
            .then(data => {
                if (data.message === 'success') {
                    fetchProducts();
                } else {
                    setMessage({ type: 'error', text: data.error || 'Fehler' });
                }
            })
            .catch(err => setMessage({ type: 'error', text: err.message }));
    };

    const downloadPDF = (invoiceId, invoiceNumber) => {
        authFetch(`/api/invoices/${invoiceId}/pdf`)
            .then(res => res.blob())
            .then(blob => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `Rechnung_${invoiceNumber}.pdf`;
                a.click();
                window.URL.revokeObjectURL(url);
            })
            .catch(err => alert('Fehler beim Download: ' + err.message));
    };

    const updateInvoiceStatus = (invoiceId, status) => {
        authFetch(`/api/invoices/${invoiceId}`, {
            method: 'PUT',
            body: { status }
        })
            .then(res => res.json())
            .then(data => {
                if (data.message === 'success') {
                    fetchInvoices();
                } else {
                    alert('Fehler: ' + data.error);
                }
            })
            .catch(err => alert('Fehler: ' + err.message));
    };

    const deleteInvoice = (invoiceId) => {
        if (!window.confirm('Rechnung wirklich loeschen?')) return;

        authFetch(`/api/invoices/${invoiceId}`, { method: 'DELETE' })
            .then(res => res.json())
            .then(data => {
                if (data.message === 'success') {
                    fetchInvoices();
                } else {
                    alert('Fehler: ' + data.error);
                }
            })
            .catch(err => alert('Fehler: ' + err.message));
    };

    const markAsSent = (invoiceId) => {
        authFetch(`/api/invoices/${invoiceId}`, {
            method: 'PUT',
            body: { sent_at: new Date().toISOString() }
        })
            .then(res => res.json())
            .then(data => {
                if (data.message === 'success') {
                    fetchInvoices();
                } else {
                    alert('Fehler: ' + data.error);
                }
            })
            .catch(err => alert('Fehler: ' + err.message));
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('de-DE');
    };

    const formatCurrency = (amount) => {
        return parseFloat(amount || 0).toFixed(2) + ' EUR';
    };

    const getStatusBadge = (status) => {
        const badges = {
            pending: { class: 'warning', text: 'Offen' },
            paid: { class: 'success', text: 'Bezahlt' },
            cancelled: { class: 'secondary', text: 'Storniert' }
        };
        const badge = badges[status] || badges.pending;
        return <span className={`badge bg-${badge.class}`}>{badge.text}</span>;
    };

    // Produkte nach Kategorie gruppieren
    const productsByCategory = products.filter(p => p.active !== false).reduce((acc, product) => {
        const cat = product.category || 'Sonstiges';
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(product);
        return acc;
    }, {});

    return (
        <div>
            <h3>Rechnungsverwaltung</h3>

            {message && (
                <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} alert-dismissible`}>
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{message.text}</pre>
                    <button type="button" className="btn-close" onClick={() => setMessage(null)}></button>
                </div>
            )}

            {/* Tabs */}
            <ul className="nav nav-tabs mb-3">
                <li className="nav-item">
                    <button
                        className={`nav-link ${activeTab === 'invoices' ? 'active' : ''}`}
                        onClick={() => setActiveTab('invoices')}
                    >
                        Alle Rechnungen ({invoices.length})
                    </button>
                </li>
                <li className="nav-item">
                    <button
                        className={`nav-link ${activeTab === 'create' ? 'active' : ''}`}
                        onClick={() => setActiveTab('create')}
                    >
                        Freie Rechnung
                    </button>
                </li>
                <li className="nav-item">
                    <button
                        className={`nav-link ${activeTab === 'products' ? 'active' : ''}`}
                        onClick={() => setActiveTab('products')}
                    >
                        Produktkatalog ({products.length})
                    </button>
                </li>
            </ul>

            {/* Tab: Alle Rechnungen */}
            {activeTab === 'invoices' && (
                <div className="table-responsive">
                    <table className="table table-striped table-hover">
                        <thead>
                            <tr>
                                <th>Nr.</th>
                                <th>Datum</th>
                                <th>Kunde</th>
                                <th>Beschreibung</th>
                                <th>Betrag</th>
                                <th>Status</th>
                                <th>Aktionen</th>
                            </tr>
                        </thead>
                        <tbody>
                            {invoices.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="text-center text-muted">
                                        Keine Rechnungen vorhanden
                                    </td>
                                </tr>
                            ) : (
                                invoices.map(invoice => (
                                    <tr key={invoice.id}>
                                        <td><strong>{invoice.invoice_number}</strong></td>
                                        <td>{formatDate(invoice.created_at)}</td>
                                        <td>
                                            {invoice.parent_name}<br/>
                                            <small className="text-muted">{invoice.parent_email}</small>
                                        </td>
                                        <td>
                                            {invoice.event_title}
                                            {invoice.child_name && (
                                                <small className="text-muted d-block">{invoice.child_name}</small>
                                            )}
                                        </td>
                                        <td>
                                            {formatCurrency(invoice.total)}
                                            {invoice.discount > 0 && (
                                                <small className="text-success d-block">
                                                    -{formatCurrency(invoice.discount)}
                                                </small>
                                            )}
                                            {invoice.vat_amount > 0 && (
                                                <small className="text-muted d-block">
                                                    inkl. {parseFloat(invoice.vat_rate)}% MwSt. ({formatCurrency(invoice.vat_amount)})
                                                </small>
                                            )}
                                        </td>
                                        <td>{getStatusBadge(invoice.status)}</td>
                                        <td>
                                            <div className="btn-group btn-group-sm">
                                                <button
                                                    className="btn btn-outline-primary"
                                                    onClick={() => downloadPDF(invoice.id, invoice.invoice_number)}
                                                    title="PDF herunterladen"
                                                >
                                                    PDF
                                                </button>
                                                {invoice.sent_at ? (
                                                    <button className="btn btn-success" disabled title={`Versendet am ${formatDate(invoice.sent_at)}`}>
                                                        Versendet
                                                    </button>
                                                ) : (
                                                    <button
                                                        className="btn btn-outline-secondary"
                                                        onClick={() => markAsSent(invoice.id)}
                                                    >
                                                        Versenden
                                                    </button>
                                                )}
                                                {invoice.status === 'pending' && (
                                                    <button
                                                        className="btn btn-outline-success"
                                                        onClick={() => updateInvoiceStatus(invoice.id, 'paid')}
                                                    >
                                                        Bezahlt
                                                    </button>
                                                )}
                                                {invoice.status === 'paid' && (
                                                    <button
                                                        className="btn btn-outline-warning"
                                                        onClick={() => updateInvoiceStatus(invoice.id, 'pending')}
                                                    >
                                                        Offen
                                                    </button>
                                                )}
                                                <button
                                                    className="btn btn-outline-danger"
                                                    onClick={() => deleteInvoice(invoice.id)}
                                                >
                                                    X
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Tab: Freie Rechnung */}
            {activeTab === 'create' && (
                <div className="row">
                    {/* Linke Seite: Formular */}
                    <div className="col-md-8">
                        {/* Kundenauswahl */}
                        <div className="card mb-3">
                            <div className="card-header d-flex justify-content-between align-items-center">
                                Kunde
                                {selectedMember && (
                                    <button className="btn btn-sm btn-outline-secondary" onClick={clearMember}>
                                        Andere Person
                                    </button>
                                )}
                            </div>
                            <div className="card-body">
                                {!selectedMember ? (
                                    <div>
                                        <div className="mb-3">
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Mitglied suchen (Name oder E-Mail)..."
                                                value={memberSearch}
                                                onChange={e => setMemberSearch(e.target.value)}
                                            />
                                            {filteredMembers.length > 0 && (
                                                <div className="list-group mt-1">
                                                    {filteredMembers.map(member => (
                                                        <button
                                                            key={member.id}
                                                            className="list-group-item list-group-item-action"
                                                            onClick={() => selectMember(member)}
                                                        >
                                                            <strong>{member.first_name} {member.last_name}</strong>
                                                            <small className="text-muted ms-2">{member.email}</small>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <hr />
                                        <small className="text-muted d-block mb-2">Oder manuell eingeben:</small>
                                        <div className="row g-2">
                                            <div className="col-md-6">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Name *"
                                                    value={manualCustomerName}
                                                    onChange={e => setManualCustomerName(e.target.value)}
                                                />
                                            </div>
                                            <div className="col-md-6">
                                                <input
                                                    type="email"
                                                    className="form-control"
                                                    placeholder="E-Mail *"
                                                    value={manualCustomerEmail}
                                                    onChange={e => setManualCustomerEmail(e.target.value)}
                                                />
                                            </div>
                                            <div className="col-12">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Adresse (optional)"
                                                    value={manualCustomerAddress}
                                                    onChange={e => setManualCustomerAddress(e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="d-flex align-items-center">
                                        <div>
                                            <strong>{manualCustomerName}</strong><br/>
                                            <small className="text-muted">{manualCustomerEmail}</small>
                                            {manualCustomerAddress && (
                                                <small className="text-muted d-block">{manualCustomerAddress}</small>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Rechnungspositionen */}
                        <div className="card mb-3">
                            <div className="card-header">Rechnungspositionen</div>
                            <div className="card-body p-0">
                                {manualItems.length === 0 ? (
                                    <div className="p-3 text-muted text-center">
                                        Noch keine Positionen. Waehle rechts aus dem Katalog oder fuege Essen/Kosten hinzu.
                                    </div>
                                ) : (
                                    <table className="table table-sm mb-0">
                                        <thead>
                                            <tr>
                                                <th>Beschreibung</th>
                                                <th>Kategorie</th>
                                                <th className="text-end">Betrag</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {manualItems.map((item, index) => (
                                                <tr key={index}>
                                                    <td>{item.description}</td>
                                                    <td>
                                                        {item.category && (
                                                            <span className="badge bg-secondary">{item.category}</span>
                                                        )}
                                                    </td>
                                                    <td className="text-end">{formatCurrency(item.price * (item.quantity || 1))}</td>
                                                    <td>
                                                        <button
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => removeItem(index)}
                                                        >
                                                            X
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot className="table-light">
                                            <tr>
                                                <td colSpan="2"><strong>Zwischensumme:</strong></td>
                                                <td className="text-end">
                                                    <strong>{formatCurrency(manualItems.reduce((sum, item) =>
                                                        sum + (item.price * (item.quantity || 1)), 0))}</strong>
                                                </td>
                                                <td></td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                )}
                            </div>
                        </div>

                        {/* Freie Position */}
                        <div className="card mb-3">
                            <div className="card-header">Position hinzufuegen</div>
                            <div className="card-body">
                                <div className="row g-2">
                                    <div className="col-md-8">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Beschreibung (z.B. 10er-Karte Programmieren, Workshop KI, ...)"
                                            value={freeItemDesc}
                                            onChange={e => setFreeItemDesc(e.target.value)}
                                        />
                                    </div>
                                    <div className="col-md-3">
                                        <input
                                            type="number"
                                            className="form-control"
                                            placeholder="Preis EUR"
                                            step="0.01"
                                            value={freeItemAmount}
                                            onChange={e => setFreeItemAmount(e.target.value)}
                                        />
                                    </div>
                                    <div className="col-md-1">
                                        <button
                                            className="btn btn-primary w-100"
                                            onClick={addFreeItem}
                                            disabled={!freeItemDesc || !freeItemAmount}
                                        >
                                            +
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Essen & Sonstige Kosten */}
                        <div className="row mb-3">
                            <div className="col-md-6">
                                <div className="card">
                                    <div className="card-header">Essen</div>
                                    <div className="card-body">
                                        <div className="row g-2">
                                            <div className="col-7">
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm"
                                                    placeholder="z.B. Mittagessen Mo-Fr"
                                                    value={quickMealDesc}
                                                    onChange={e => setQuickMealDesc(e.target.value)}
                                                />
                                            </div>
                                            <div className="col-3">
                                                <input
                                                    type="number"
                                                    className="form-control form-control-sm"
                                                    placeholder="EUR"
                                                    step="0.01"
                                                    value={quickMealAmount}
                                                    onChange={e => setQuickMealAmount(e.target.value)}
                                                />
                                            </div>
                                            <div className="col-2">
                                                <button
                                                    className="btn btn-sm btn-primary w-100"
                                                    onClick={addMeal}
                                                    disabled={!quickMealDesc || !quickMealAmount}
                                                >
                                                    +
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="card">
                                    <div className="card-header">Sonstiges</div>
                                    <div className="card-body">
                                        <div className="row g-2">
                                            <div className="col-7">
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm"
                                                    placeholder="z.B. Materialkosten"
                                                    value={quickOtherDesc}
                                                    onChange={e => setQuickOtherDesc(e.target.value)}
                                                />
                                            </div>
                                            <div className="col-3">
                                                <input
                                                    type="number"
                                                    className="form-control form-control-sm"
                                                    placeholder="EUR"
                                                    step="0.01"
                                                    value={quickOtherAmount}
                                                    onChange={e => setQuickOtherAmount(e.target.value)}
                                                />
                                            </div>
                                            <div className="col-2">
                                                <button
                                                    className="btn btn-sm btn-primary w-100"
                                                    onClick={addOther}
                                                    disabled={!quickOtherDesc || !quickOtherAmount}
                                                >
                                                    +
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Rabatt & Notizen */}
                        <div className="card mb-3">
                            <div className="card-header">Rabatt & Notizen</div>
                            <div className="card-body">
                                <div className="row g-2">
                                    <div className="col-md-3">
                                        <input
                                            type="number"
                                            className="form-control"
                                            placeholder="Rabatt EUR"
                                            step="0.01"
                                            value={manualDiscount}
                                            onChange={e => setManualDiscount(e.target.value)}
                                        />
                                    </div>
                                    <div className="col-md-5">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Rabattgrund"
                                            value={manualDiscountReason}
                                            onChange={e => setManualDiscountReason(e.target.value)}
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Interne Notizen"
                                            value={manualNotes}
                                            onChange={e => setManualNotes(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="row g-2 mt-1 align-items-center">
                                    <div className="col-auto">
                                        <div className="form-check">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                id="manualApplyVat"
                                                checked={manualApplyVat}
                                                onChange={e => setManualApplyVat(e.target.checked)}
                                            />
                                            <label className="form-check-label" htmlFor="manualApplyVat">
                                                zzgl. MwSt.
                                            </label>
                                        </div>
                                    </div>
                                    {manualApplyVat && (
                                        <div className="col-auto">
                                            <div className="input-group input-group-sm" style={{ width: '110px' }}>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    step="0.1"
                                                    value={manualVatRate}
                                                    onChange={e => setManualVatRate(e.target.value)}
                                                />
                                                <span className="input-group-text">%</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Gesamtbetrag & Erstellen */}
                        <div className="card">
                            <div className="card-body d-flex justify-content-between align-items-center">
                                <div>
                                    {manualApplyVat ? (
                                        <>
                                            <div>Zwischensumme (netto): {formatCurrency(calculateNetTotal())}</div>
                                            <div>zzgl. {parseFloat(manualVatRate) || 0}% MwSt.: {formatCurrency(calculateVatAmount())}</div>
                                            <h4 className="mb-0 mt-1">
                                                Gesamt (brutto): <strong>{formatCurrency(calculateTotal())}</strong>
                                            </h4>
                                        </>
                                    ) : (
                                        <h4 className="mb-0">
                                            Gesamt: <strong>{formatCurrency(calculateTotal())}</strong>
                                        </h4>
                                    )}
                                    {parseFloat(manualDiscount) > 0 && (
                                        <small className="text-success">inkl. -{formatCurrency(manualDiscount)} Rabatt</small>
                                    )}
                                </div>
                                <button
                                    className="btn btn-success btn-lg"
                                    onClick={createInvoice}
                                    disabled={loading || !manualCustomerName || !manualCustomerEmail || manualItems.length === 0}
                                >
                                    {loading ? 'Wird erstellt...' : 'Rechnung erstellen'}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Rechte Seite: Produkte & Events */}
                    <div className="col-md-4">
                        {/* Produktkatalog */}
                        <div className="card mb-3">
                            <div className="card-header">Aus Katalog</div>
                            <div className="card-body p-0" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                {Object.keys(productsByCategory).length === 0 ? (
                                    <div className="p-3 text-muted text-center">
                                        Keine Produkte im Katalog
                                    </div>
                                ) : (
                                    Object.entries(productsByCategory).map(([category, prods]) => (
                                        <div key={category}>
                                            <div className="px-3 py-1 bg-light border-bottom">
                                                <small className="text-muted fw-bold">{category}</small>
                                            </div>
                                            {prods.map(product => (
                                                <button
                                                    key={product.id}
                                                    className="list-group-item list-group-item-action d-flex justify-content-between"
                                                    onClick={() => addProductToInvoice(product)}
                                                >
                                                    <span>{product.name}</span>
                                                    <span className="badge bg-primary">{formatCurrency(product.price)}</span>
                                                </button>
                                            ))}
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                        {/* Feriencamps */}
                        {feriencamps.length > 0 && (
                            <div className="card mb-3">
                                <div className="card-header">Feriencamps</div>
                                <div className="card-body p-0" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                    {feriencamps.map(event => (
                                        <button
                                            key={event.id}
                                            className="list-group-item list-group-item-action"
                                            onClick={() => addFeriencampToInvoice(event)}
                                        >
                                            <div className="d-flex justify-content-between">
                                                <strong>{event.title}</strong>
                                                <span className="badge bg-primary">{formatCurrency(event.price)}</span>
                                            </div>
                                            <small className="text-muted">
                                                {formatDate(event.start_date)} - {formatDate(event.end_date)}
                                            </small>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Workshops */}
                        {workshops.length > 0 && (
                            <div className="card">
                                <div className="card-header">Workshops / Events</div>
                                <div className="card-body p-0" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                    {workshops.map(workshop => (
                                        <button
                                            key={workshop.id}
                                            className="list-group-item list-group-item-action"
                                            onClick={() => addWorkshopToInvoice(workshop)}
                                        >
                                            <div className="d-flex justify-content-between">
                                                <strong>{workshop.title}</strong>
                                                <span className="badge bg-success">{formatCurrency(workshop.price)}</span>
                                            </div>
                                            <small className="text-muted">
                                                {workshop.date ? formatDate(workshop.date) : workshop.dates_display || workshop.target_audience}
                                            </small>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Tab: Produktkatalog */}
            {activeTab === 'products' && (
                <div>
                    {/* Neues Produkt */}
                    <div className="card mb-4">
                        <div className="card-header">Neues Produkt</div>
                        <div className="card-body">
                            <div className="row g-3">
                                <div className="col-md-4">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Name *"
                                        value={newProduct.name}
                                        onChange={e => setNewProduct({ ...newProduct, name: e.target.value })}
                                    />
                                </div>
                                <div className="col-md-3">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Kategorie"
                                        value={newProduct.category}
                                        onChange={e => setNewProduct({ ...newProduct, category: e.target.value })}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <input
                                        type="number"
                                        className="form-control"
                                        step="0.01"
                                        placeholder="Preis *"
                                        value={newProduct.price}
                                        onChange={e => setNewProduct({ ...newProduct, price: e.target.value })}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <select
                                        className="form-select"
                                        value={newProduct.unit}
                                        onChange={e => setNewProduct({ ...newProduct, unit: e.target.value })}
                                    >
                                        <option value="Stueck">Stueck</option>
                                        <option value="Stunde">Stunde</option>
                                        <option value="Tag">Tag</option>
                                        <option value="Pauschal">Pauschal</option>
                                    </select>
                                </div>
                                <div className="col-md-1">
                                    <button
                                        className="btn btn-primary w-100"
                                        onClick={createProduct}
                                        disabled={!newProduct.name || !newProduct.price}
                                    >
                                        +
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Events importieren */}
                    {(feriencamps.length > 0 || workshops.length > 0) && (
                        <div className="card mb-4">
                            <div className="card-header">Events als Produkte importieren</div>
                            <div className="card-body">
                                <p className="text-muted mb-2">
                                    Klicke auf ein Event um es als Produkt in den Katalog aufzunehmen:
                                </p>

                                {feriencamps.length > 0 && (
                                    <div className="mb-3">
                                        <small className="text-muted d-block mb-1">Feriencamps:</small>
                                        <div className="d-flex flex-wrap gap-2">
                                            {feriencamps.map(event => (
                                                <button
                                                    key={event.id}
                                                    className="btn btn-outline-primary btn-sm"
                                                    onClick={() => importFeriencampAsProduct(event)}
                                                >
                                                    {event.title} ({formatCurrency(event.price)})
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {workshops.length > 0 && (
                                    <div>
                                        <small className="text-muted d-block mb-1">Workshops / Events:</small>
                                        <div className="d-flex flex-wrap gap-2">
                                            {workshops.map(workshop => (
                                                <button
                                                    key={workshop.id}
                                                    className="btn btn-outline-success btn-sm"
                                                    onClick={() => importWorkshopAsProduct(workshop)}
                                                >
                                                    {workshop.title} ({formatCurrency(workshop.price)})
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Produktliste */}
                    <div className="card">
                        <div className="card-header">Produktkatalog ({products.length} Produkte)</div>
                        <div className="card-body p-0">
                            {products.length === 0 ? (
                                <div className="p-4 text-center text-muted">
                                    Noch keine Produkte vorhanden.
                                </div>
                            ) : (
                                <table className="table table-striped mb-0">
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>Kategorie</th>
                                            <th>Preis</th>
                                            <th>Einheit</th>
                                            <th>Status</th>
                                            <th>Aktionen</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {products.map(product => (
                                            <tr key={product.id}>
                                                {editingProduct?.id === product.id ? (
                                                    <>
                                                        <td>
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-sm"
                                                                value={editingProduct.name}
                                                                onChange={e => setEditingProduct({ ...editingProduct, name: e.target.value })}
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-sm"
                                                                value={editingProduct.category || ''}
                                                                onChange={e => setEditingProduct({ ...editingProduct, category: e.target.value })}
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                className="form-control form-control-sm"
                                                                step="0.01"
                                                                value={editingProduct.price}
                                                                onChange={e => setEditingProduct({ ...editingProduct, price: e.target.value })}
                                                            />
                                                        </td>
                                                        <td>
                                                            <select
                                                                className="form-select form-select-sm"
                                                                value={editingProduct.unit}
                                                                onChange={e => setEditingProduct({ ...editingProduct, unit: e.target.value })}
                                                            >
                                                                <option value="Stueck">Stueck</option>
                                                                <option value="Stunde">Stunde</option>
                                                                <option value="Tag">Tag</option>
                                                                <option value="Pauschal">Pauschal</option>
                                                            </select>
                                                        </td>
                                                        <td>
                                                            <select
                                                                className="form-select form-select-sm"
                                                                value={editingProduct.active === false ? 'false' : 'true'}
                                                                onChange={e => setEditingProduct({ ...editingProduct, active: e.target.value === 'true' })}
                                                            >
                                                                <option value="true">Aktiv</option>
                                                                <option value="false">Inaktiv</option>
                                                            </select>
                                                        </td>
                                                        <td>
                                                            <div className="btn-group btn-group-sm">
                                                                <button className="btn btn-success" onClick={() => updateProduct(editingProduct)}>
                                                                    OK
                                                                </button>
                                                                <button className="btn btn-secondary" onClick={() => setEditingProduct(null)}>
                                                                    X
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </>
                                                ) : (
                                                    <>
                                                        <td><strong>{product.name}</strong></td>
                                                        <td>{product.category || '-'}</td>
                                                        <td>{formatCurrency(product.price)}</td>
                                                        <td>{product.unit}</td>
                                                        <td>
                                                            {product.active === false ? (
                                                                <span className="badge bg-secondary">Inaktiv</span>
                                                            ) : (
                                                                <span className="badge bg-success">Aktiv</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <div className="btn-group btn-group-sm">
                                                                <button
                                                                    className="btn btn-outline-primary"
                                                                    onClick={() => setEditingProduct({ ...product })}
                                                                >
                                                                    Bearbeiten
                                                                </button>
                                                                <button
                                                                    className="btn btn-outline-danger"
                                                                    onClick={() => deleteProduct(product.id)}
                                                                >
                                                                    X
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default RechnungenManager;
